import { LicenseModel } from '../../../../core/domainModels/licences/LicenseModel';
import { LicenseTypeEnum } from '../../../../core/domainModels/licences/LicenseType';
import { TimeZone } from '../../../../core/helpers/timeZoneEnum';
import { AddLicenseVersion } from '../../../../core/domainModels/licenceVersions/AddLicenseVersion';
import { UpdateLicenseVersion } from '../../../../core/domainModels/licenceVersions/UpdateLicenseVersion';
import { TachoModuleConsts } from 'tacho-models';
import { Item } from 'tacho-ui';
import { GenerateLicenseModel } from '../../../../core/domainModels/licences/GenerateLicense';

export const maxInt = 2147483647;

export const checkVersionOverlapUpdate = (licence: LicenseModel, actualLicence: UpdateLicenseVersion) => {
    const sortedLicenceVersions = [...licence?.licenseVersions]?.sort((a, b) => a.validFrom.diff(b.validFrom).milliseconds);

    const versionsWithEdit = sortedLicenceVersions.map(v =>
        v.id === actualLicence.licenseVersionId
            ? { ...v, validFrom: actualLicence.validFrom!, validTo: actualLicence.validTo! }
            : v,
    );
    const sortedWithEdit = [...versionsWithEdit].sort((a, b) => a.validFrom.diff(b.validFrom).milliseconds);

    for (let i = 1; i < sortedWithEdit.length; i++) {
        const prev = sortedWithEdit[i - 1];
        const curr = sortedWithEdit[i];
        const expectedCurrValidFrom = prev.validTo.plus({ days: 1 }).startOf('day');
        const actualCurrValidFrom = curr.validFrom.startOf('day');
        if (actualCurrValidFrom.toMillis() < prev.validTo.startOf('day').toMillis()) {
            return 'licenceVersionsCannotOverlap';
        }
        if (actualCurrValidFrom.toMillis() !== expectedCurrValidFrom.toMillis()) {
            return 'licenceVersionsMustBeContinuous';
        }
    }
    return undefined;
};

export const checkVersionOverlapAdd = (licence: LicenseModel, actualLicence: AddLicenseVersion) => {
    const lastVersion = licence?.licenseVersions?.reduce((a, b) => (a.validFrom > b.validFrom ? a : b));
    if (!lastVersion || !actualLicence.validFrom?.isValid) return undefined;

    const expectedValidFrom = lastVersion.validTo.plus({ days: 1 }).startOf('day');
    const actualValidFrom = actualLicence.validFrom.setZone(TimeZone.Europe_Warsaw).startOf('day');

    if (actualValidFrom.toMillis() <= lastVersion.validTo.startOf('day').toMillis()) {
        return 'licenceVersionsCannotOverlap';
    }
    if (actualValidFrom.toMillis() !== expectedValidFrom.toMillis()) {
        return 'licenceVersionsMustBeContinuous';
    }
    return undefined;
};

export const modulesChange = (
    selectedItems: Array<Item>,
    prevState: AddLicenseVersion | UpdateLicenseVersion | GenerateLicenseModel,
) => {
    const recordsModuleId = TachoModuleConsts.recordsModuleId.toLowerCase();
    const foreignRecordsId = TachoModuleConsts.foreignRecordsModuleId.toLowerCase();
    let items: Array<Item> = selectedItems;

    const recordsModuleIndex = items.findIndex(item => item.id === recordsModuleId);
    const foreignRecordsModuleIndex = items.findIndex(item => item.id === foreignRecordsId);

    let recordsModuleIsChecked = items.find(item => item.id === recordsModuleId)?.checked;
    let foreignRecordsModuleIsChecked = items.find(item => item.id === foreignRecordsId)?.checked;

    let isBothSelected =
        prevState.allowedModules?.find(item => item.tachoModuleId === recordsModuleId) &&
        prevState.allowedModules?.find(item => item.tachoModuleId === foreignRecordsId);

    if (isBothSelected && recordsModuleIsChecked === false) {
        items[foreignRecordsModuleIndex] = { ...items[foreignRecordsModuleIndex], checked: false };
        items[recordsModuleIndex] = { ...items[recordsModuleIndex], checked: false };
    } else if (isBothSelected && foreignRecordsModuleIsChecked === false) {
        items[foreignRecordsModuleIndex] = { ...items[foreignRecordsModuleIndex], checked: false };
    } else if (foreignRecordsModuleIsChecked) {
        items[foreignRecordsModuleIndex] = { ...items[foreignRecordsModuleIndex], checked: true };
        items[recordsModuleIndex] = { ...items[recordsModuleIndex], checked: true };
    }

    return items;
};

export const isDeveloperLicenseType = (type?: LicenseTypeEnum) => type === LicenseTypeEnum.Developer;

export const getDistributorIdForLicenseType = (type: LicenseTypeEnum | undefined, distributorId?: string | null) =>
    isDeveloperLicenseType(type) ? null : distributorId;
